import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router';

const STORAGE_PREFIX = 'school:scroll:';
const MAX_ENTRIES = 30;

/** Reads/writes a bounded ring of scroll positions keyed by location.key. */
function readMap(): Record<string, number> {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + 'map');
    return raw ? (JSON.parse(raw) as Record<string, number>) : {};
  } catch {
    return {};
  }
}

function writeMap(map: Record<string, number>) {
  try {
    const keys = Object.keys(map);
    if (keys.length > MAX_ENTRIES) {
      // Drop the oldest entries — insertion order is preserved on JSON parse.
      const drop = keys.slice(0, keys.length - MAX_ENTRIES);
      drop.forEach((k) => delete map[k]);
    }
    sessionStorage.setItem(STORAGE_PREFIX + 'map', JSON.stringify(map));
  } catch {
    /* sessionStorage full / unavailable — non-fatal */
  }
}

/**
 * Preserves window scroll across back/forward navigation. On POP (back/forward)
 * restores the saved offset for `location.key`; on PUSH/REPLACE scrolls to top.
 * Saves the current offset just before leaving the page.
 *
 * Mount once near the layout root (already done in <AppLayout>).
 */
export function useScrollRestoration() {
  const location = useLocation();
  const navType = useNavigationType();

  // Save scroll position when leaving the current location.
  useEffect(() => {
    const key = location.key;
    const save = () => {
      const map = readMap();
      map[key] = window.scrollY;
      writeMap(map);
    };
    window.addEventListener('beforeunload', save);
    return () => {
      save();
      window.removeEventListener('beforeunload', save);
    };
  }, [location.key]);

  // Restore on POP, scroll-to-top on PUSH/REPLACE.
  // Content typically streams in (TanStack Query placeholderData → fresh fetch),
  // so the target Y might exceed scrollHeight on the first frame. Retry over
  // ~600ms until either the scroll sticks or we time out.
  useEffect(() => {
    if (navType !== 'POP') {
      window.scrollTo(0, 0);
      return;
    }
    const map = readMap();
    const target = map[location.key] ?? 0;
    if (target === 0) {
      window.scrollTo(0, 0);
      return;
    }

    let cancelled = false;
    const deadline = performance.now() + 600;
    const tryScroll = () => {
      if (cancelled) return;
      window.scrollTo(0, target);
      const reached = Math.abs(window.scrollY - target) < 4;
      if (reached || performance.now() > deadline) return;
      requestAnimationFrame(tryScroll);
    };
    requestAnimationFrame(tryScroll);
    return () => {
      cancelled = true;
    };
  }, [location.key, navType]);
}
